import { BindAll } from 'lodash-decorators'
import { $service } from '@xt/client/experimental/request/service'
import { action, observable } from 'mobx'
import { EnumApiHomework } from '@xt/client/api'
import type { WaterfallMoment } from './types.d'

type FetchStatus = 'loading' | 'no-more'

export type Tabs = 'find' | 'homework' | 'dairy'

export enum EnumLoadResult {
  /**
   * 异常取消
   */
  Cancel,
  /**
   * 接口调用失败
   */
  Error,
  /**
   * 数据加载成功
   */
  Loaded,
  /**
   * 没有下一页数据了
   */
  Complete
}

type CacheValue = {
  page: number
  pageSize: number
  list: Array<WaterfallMoment>
  hasMore: boolean
  inited: boolean
}

function getInitCacheValue(): CacheValue {
  return { page: 0, pageSize: 20, list: [], hasMore: true, inited: false }
}

@BindAll()
export class CourseWaterfall {
  @observable fetchStatus: FetchStatus = 'loading'
  @observable currentList: Array<WaterfallMoment> = []
  private currentTab: Tabs = 'find'

  isRequesting = false

  // 初始化时间 用户缓存
  launchTime: null | number = null

  // 将列表数据缓存下来
  private _cache: Record<Tabs, CacheValue> = {
    find: getInitCacheValue(),
    dairy: getInitCacheValue(),
    homework: getInitCacheValue()
  }

  private getCache(tab: Tabs) {
    return this._cache[tab]
  }

  private setCache(tab: Tabs, value: CacheValue) {
    this._cache[tab] = value
  }

  // 在指定瀑布流的开头追加一个日记
  public unshiftMomentItemToTab(tab: Tabs, moment: WaterfallMoment) {
    // 更新缓存
    const old = this.getCache(tab)
    this.setCache(tab, { ...old, list: [moment, ...old.list] })

    // 如果当前锚定的是追加的tab，则需要更新currentList 使页面刷新
    if (this.currentTab === tab) {
      const { list } = this.getCache(tab)
      this.setCurrentList(list)
    }
  }

  @action
  private setFetchStatus(status: FetchStatus) {
    this.fetchStatus = status
  }

  @action
  private setCurrentList(list: Array<WaterfallMoment>) {
    this.currentList = list
  }

  async switchTab(tab: Tabs) {
    this.currentTab = tab
    const { inited, list } = this.getCache(tab)

    // 初始化
    if (!inited) {
      this.setCurrentList([])
    } else {
      // 取缓存数据先渲染
      this.setCurrentList(list)
    }
  }

  async loadMore(tab: Tabs, courseId: string | number) {
    const res: EnumLoadResult | undefined = await this.fetchMomentListByTab(tab, courseId)
    const { list: newList } = this.getCache(tab)
    this.setCurrentList(newList)
    return res
  }

  // 删除瀑布流中对应tabs的第一个元素
  @action
  public removeFirstItem(tab: Tabs) {
    // 更新cache
    const old = this.getCache(tab)
    const list = old.list
    list.shift()
    this.setCache(tab, { ...old, list: [...list] })

    // 更新当前页面数据
    this.currentList.shift()
  }

  // 删除瀑布流中的item
  @action
  public removeItem(momentId: string) {
    // 更新cache
    const cacheKeys: Array<Tabs> = ['find', 'dairy', 'homework']
    cacheKeys.forEach(key => {
      const old = this.getCache(key)
      old.list = old.list.filter(i => i.id !== momentId)
      this.setCache(key, old)
    })

    // 更新当前页面数据
    this.currentList = this.currentList.filter(i => i.id !== momentId)
  }

  // 用户在瀑布流卡片位置触发点赞动作
  public async onLikeMoment(moment: WaterfallMoment) {
    if (moment.like === true) {
      throw new Error('你已经点过赞了')
    }

    this.likeItem(moment.id)

    const [, success] = await $service<boolean>({
      url: EnumApiHomework.MomentLike,
      method: 'PUT',
      data: { momentId: moment.id }
    })

    if (success === true) {
      return
    } else {
      this.cancelLikeItem(moment.id)
      throw new Error('点赞失败')
    }
  }

  @action
  public likeItem(momentId: string) {
    // 更新cache
    const cacheKeys: Array<Tabs> = ['find', 'dairy', 'homework']
    cacheKeys.forEach(key => {
      const old = this.getCache(key)
      old.list = old.list.map(item => {
        if (item.id === momentId) {
          item.like = true
          item.likeCount = typeof item.likeCount === 'number' ? item.likeCount + 1 : 1
        }

        return item
      })
      this.setCache(key, old)
    })

    // 更新当前页面数据
    this.currentList = this.currentList.map(item => {
      if (item.id === momentId) {
        item.like = true
        item.likeCount = typeof item.likeCount === 'number' ? item.likeCount + 1 : 1
      }

      return item
    })
  }

  // 乐观更新失败后回滚点赞数据
  @action
  public cancelLikeItem(momentId: string) {
    // 更新cache
    const cacheKeys: Array<Tabs> = ['find', 'dairy', 'homework']
    cacheKeys.forEach(key => {
      const old = this.getCache(key)
      old.list = old.list.map(item => {
        if (item.id === momentId) {
          item.like = false
          item.likeCount = typeof item.likeCount === 'number' ? Math.max(0, item.likeCount - 1) : 0
        }

        return item
      })
      this.setCache(key, old)
    })

    // 更新当前页面数据
    this.currentList = this.currentList.map(item => {
      if (item.id === momentId) {
        item.like = false
        item.likeCount = typeof item.likeCount === 'number' ? Math.max(0, item.likeCount - 1) : 0
      }

      return item
    })
  }

  private async fetchMomentListByTab(tab: Tabs, courseId: string | number) {
    if (!this.launchTime) {
      this.launchTime = Date.now()
    }

    if (this.isRequesting) {
      return EnumLoadResult.Cancel
    }

    try {
      this.isRequesting = true

      const { page, pageSize, hasMore, list } = this.getCache(tab)

      if (!hasMore) {
        this.setFetchStatus('no-more')
        return EnumLoadResult.Complete
      }

      this.setFetchStatus('loading')
      const [err, result] = await $service<{ data: Array<WaterfallMoment> }>({
        method: 'GET',
        url: EnumApiHomework.MomentListV3,
        data: {
          sourceType: 3,
          sourceId: courseId
        },
        params: {
          firstRequestTime: this.launchTime,
          sourceType: 3,
          page: page + 1,
          pageSize,
          sourceId: courseId,
          tabId: tab === 'homework' ? 1 : tab === 'dairy' ? 2 : undefined
        }
      })

      if (err || !Array.isArray(result.data)) return EnumLoadResult.Error

      this.setCache(tab, {
        page: page + 1,
        pageSize,
        hasMore: result.data.length === pageSize,
        list: [...list, ...result.data],
        inited: true
      })

      // 是否还有更多数据状态处理
      return result.data.length === pageSize ? EnumLoadResult.Loaded : EnumLoadResult.Complete
    } finally {
      this.isRequesting = false
    }
  }

  @action
  reset() {
    this.fetchStatus = 'loading'
    this.currentList = []
    this.isRequesting = false
    this._cache = {
      find: getInitCacheValue(),
      dairy: getInitCacheValue(),
      homework: getInitCacheValue()
    }
  }
}
